import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Loader2, Copy, Zap, Clock } from "lucide-react";
import api, { errMsg } from "../lib/api";
import { formatRupiah, formatDateTime } from "../lib/format";
import { AmountInput, QuickAmounts } from "../components/AmountInput";

export default function TopUp() {
  const navigate = useNavigate();
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState("");
  const [amount, setAmount] = useState(0);
  const [creating, setCreating] = useState(false);
  const [va, setVa] = useState(null);
  const [vas, setVas] = useState([]);
  const [simulating, setSimulating] = useState("");

  const loadVas = () => api.get("/topup/va").then((r) => setVas(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/banks").then((r) => setBanks(r.data));
    loadVas();
  }, []);

  const createVa = async () => {
    if (!bankCode) {
      toast.error("Pilih bank terlebih dahulu");
      return;
    }
    if (amount < 10000) {
      toast.error("Minimal top up Rp 10.000");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/topup/va", { bank_code: bankCode, amount });
      setVa(res.data);
      loadVas();
      toast.success("Virtual Account berhasil dibuat!");
    } catch (err) {
      toast.error(errMsg(err, "Gagal membuat VA"));
    } finally {
      setCreating(false);
    }
  };

  const copyVa = (number) => {
    navigator.clipboard.writeText(number);
    toast.success("Nomor VA disalin");
  };

  const simulate = async (vaId) => {
    setSimulating(vaId);
    try {
      const res = await api.post(`/topup/va/${vaId}/simulate-payment`);
      toast.success("Pembayaran diterima! Saldo bertambah.");
      navigate(`/transaksi/${res.data.transaction.id}`);
    } catch (err) {
      toast.error(errMsg(err, "Gagal mensimulasikan pembayaran"));
      loadVas();
    } finally {
      setSimulating("");
    }
  };

  const activeVas = vas.filter((v) => v.status === "ACTIVE" && v.id !== va?.id);

  return (
    <div className="max-w-xl mx-auto space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-semibold">Top Up Saldo</h1>
        <p className="text-sm text-muted-foreground">Isi saldo lewat Virtual Account bank pilihanmu.</p>
      </div>

      {!va && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="rounded-2xl">
            <CardContent className="p-5 space-y-4">
              <div className="space-y-1.5">
                <Label>Bank</Label>
                <Select value={bankCode} onValueChange={setBankCode}>
                  <SelectTrigger className="h-11" data-testid="topup-va-bank-select">
                    <SelectValue placeholder="Pilih bank untuk VA" />
                  </SelectTrigger>
                  <SelectContent>
                    {banks.map((b) => (
                      <SelectItem key={b.code} value={b.code} data-testid={`topup-bank-option-${b.code}`}>{b.name} — {b.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Nominal Top Up</Label>
                <AmountInput value={amount} onChange={setAmount} testId="topup-amount-input" />
                <p className="text-xs text-muted-foreground">Minimal Rp 10.000 · Maksimal Rp 20.000.000</p>
              </div>
              <QuickAmounts onPick={setAmount} amounts={[100000, 250000, 500000, 1000000]} />
              <Button className="w-full h-12 text-base" onClick={createVa} disabled={creating || !bankCode || amount < 10000} data-testid="topup-create-va-button">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buat Virtual Account"}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {va && (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
          <Card className="rounded-2xl border-primary/30 shadow-[0_10px_30px_rgba(30,91,255,0.1)]">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold">Virtual Account {va.bank_name}</h3>
                <Badge variant="outline" className="bg-amber-50 text-amber-600 border-amber-100">Menunggu Pembayaran</Badge>
              </div>
              <div className="rounded-xl bg-secondary p-4">
                <p className="text-xs text-muted-foreground mb-1">Nomor Virtual Account</p>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-mono text-xl font-semibold tracking-wide" data-testid="topup-va-number">{va.va_number}</p>
                  <Button size="sm" variant="secondary" onClick={() => copyVa(va.va_number)} data-testid="topup-va-copy-button" aria-label="Salin nomor VA">
                    <Copy className="h-4 w-4 mr-1" /> Salin
                  </Button>
                </div>
                <p className="text-sm mt-2">Nominal: <span className="font-semibold tabular-nums" data-testid="topup-va-amount">{formatRupiah(va.amount)}</span></p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Berlaku hingga {formatDateTime(va.expires_at)}</p>
              </div>
              <Accordion type="single" collapsible>
                <AccordionItem value="cara" className="border rounded-xl px-4">
                  <AccordionTrigger className="text-sm py-3">Cara Pembayaran</AccordionTrigger>
                  <AccordionContent className="text-xs text-muted-foreground space-y-1.5">
                    <p>1. Buka aplikasi m-banking / ATM {va.bank_name}.</p>
                    <p>2. Pilih menu Transfer &gt; Virtual Account.</p>
                    <p>3. Masukkan nomor VA <span className="font-mono">{va.va_number}</span>.</p>
                    <p>4. Periksa nama dan nominal, lalu konfirmasi pembayaran.</p>
                    <p>5. Saldo Quantum Pay kamu otomatis bertambah setelah pembayaran.</p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
              <div className="rounded-xl border border-teal-200 bg-teal-50 p-3 text-xs text-muted-foreground">
                <span className="font-semibold text-[#0FA5A0]">Mode Sandbox:</span> gunakan tombol di bawah untuk mensimulasikan bank mengirim pembayaran ke VA ini.
              </div>
              <Button className="w-full h-12 text-base bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] hover:opacity-90" onClick={() => simulate(va.id)} disabled={simulating === va.id} data-testid="topup-va-simulate-payment-button">
                {simulating === va.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Zap className="h-4 w-4 mr-1.5" /> Simulasikan Pembayaran</>)}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => { setVa(null); setAmount(0); }} data-testid="topup-create-another-button">
                Buat VA Lain
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {activeVas.length > 0 && (
        <Card className="rounded-2xl">
          <CardContent className="p-5">
            <h3 className="font-heading font-semibold mb-3">VA Aktif Lainnya</h3>
            <div className="space-y-2">
              {activeVas.map((v) => (
                <div key={v.id} className="rounded-xl border p-3 flex items-center justify-between gap-2" data-testid="active-va-row">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-semibold truncate">{v.va_number}</p>
                    <p className="text-xs text-muted-foreground">{v.bank_name} · {formatRupiah(v.amount)}</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => copyVa(v.va_number)} aria-label="Salin"><Copy className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" className="bg-gradient-to-r from-[#2E7CF6] to-[#1FC8A9] hover:opacity-90" onClick={() => simulate(v.id)} disabled={simulating === v.id} data-testid="active-va-simulate-button">
                      {simulating === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Bayar"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
